import { SolIcon } from '@sol/icons';
import { EnumTools } from './enum-tools';
import { SolType } from '../models/core/sol-type';

export class ObjectTools {
  /**
   * Returns true if there is deep equality between the given objects.
   *
   * Two objects are equal when they share a constructor and their own enumerable keys match one for one, so a class
   * instance never equals a literal and `{ a: 1 }` never equals `{ a: 1, b: undefined }`. Arrays compare by length and
   * index, `Date` by time, `RegExp` by source and flags, `Map` and `Set` by content, and `NaN` equals `NaN`. Functions
   * compare by reference. A class that overrides `valueOf` or `toString` is compared through that override, which is
   * what lets two freshly built definitions carrying the same `SolType` still read as equal.
   */
  public static equals(current: any, next: any): boolean {
    return ObjectTools.deepEquals(current, next, true);
  }

  /**
   * Returns true if there is deep inequality between the given objects.
   */
  public static differs(current: any, next: any): boolean {
    return !ObjectTools.deepEquals(current, next, true);
  }

  /**
   * Returns true when `next` may be replaced by `current` without any consumer being able to tell them apart.
   *
   * This is `equals` without its `valueOf`/`toString` shortcut: `SolType` renders as the same text whatever it holds,
   * so every SolType is `equals` to every other, and handing a component the previous one would give it the wrong
   * converter. Used where a reference is reused rather than merely compared.
   */
  public static interchangeable(current: any, next: any): boolean {
    return ObjectTools.deepEquals(current, next, false);
  }

  /**
   * Creates a shallow clone preserving the prototype and overriding provided properties.
   */
  public static copy<V>(current: V, props: Partial<V> = {}): V {
    const clone = Object.assign(Object.create(Object.getPrototypeOf(current)), current);
    return Object.assign(clone, props);
  }

  /**
   * Return true of the given object is null or undefined.
   */
  public static isNull(obj: any): boolean {
    return obj === null || obj === undefined;
  }

  /**
   * Returns the given object if it is not null or undefined, otherwise returns the default value.
   */
  public static ifNull(obj: any, defaultValue: any): any {
    return ObjectTools.isNull(obj) ? defaultValue : obj;
  }

  /**
   * Returns the given value as a number if it is a valid number, otherwise the fallback (or undefined without
   * one). A blank string is not a number.
   */
  public static toNumber(value: unknown): number | undefined;
  public static toNumber(value: unknown, fallback: number): number;
  public static toNumber(value: unknown, fallback?: number): number | undefined {
    if (ObjectTools.isNull(value) || (typeof value === 'string' && value.trim() === '')) {
      return fallback;
    }
    try {
      const number = Number(value);
      return isNaN(number) ? fallback : number;
    } catch (e) {
      return fallback;
    }
  }

  /**
   * Returns an array of numbers from 0 to the given length.
   */
  public static indexArray(length: number): number[] {
    return Array.from({ length: length }, (_, i) => i);
  }

  /**
   * Returns the first element or a given default (default: undefined)
   */
  public static findFirst<V>(array: V[] | undefined, defaultValue: V | undefined = undefined): V | undefined {
    return array !== undefined && array.length > 0 ? array[0] : defaultValue;
  }

  /**
   * Returns the last element or a given default (default: undefined)
   */
  public static findLast<V>(array: V[] | undefined, defaultValue: V | undefined = undefined): V | undefined {
    return array !== undefined && array.length > 0 ? array[array.length - 1] : defaultValue;
  }

  /**
   * Returns the given value (generified identity function).
   */
  public static identity<V>(value: V | undefined): V | undefined {
    return value;
  }

  /**
   * Serializes any object to a JSON string when defined.
   */
  public static objectToString(value: any | undefined): string | undefined {
    return value === undefined ? undefined : JSON.stringify(value);
  }

  /**
   * Parses a JSON string back into an object when provided.
   */
  public static objectFromString(value: string | undefined): any | undefined {
    return value === undefined ? undefined : JSON.parse(value);
  }

  /**
   * Converts a number to its string representation or undefined.
   */
  public static numberToString(value: number | undefined): string | undefined {
    return value === undefined ? undefined : value.toString();
  }

  /**
   * Parses a numeric string and returns the corresponding number.
   */
  public static numberFromString(value: string | undefined): number | undefined {
    if (!value) {
      return undefined;
    }
    const candidate = Number(value);
    if (Number.isNaN(candidate)) {
      throw new Error(`Invalid number: ${value}`);
    }
    return candidate;
  }

  /**
   * Converts a boolean to its string equivalent.
   */
  public static booleanToString(value: boolean | undefined): string | undefined {
    return value === undefined ? undefined : value ? 'true' : 'false';
  }

  /**
   * Parses a string into a boolean flag when possible.
   */
  public static booleanFromString(value: string | undefined): boolean | undefined {
    return value === undefined ? undefined : value === 'true';
  }

  /**
   * Serializes a Date to UTC date string (YYYY-MM-DD) for API requests.
   */
  public static dateToString(value: Date | undefined): string | undefined {
    return value === undefined ? undefined : value.toISOString().split('T')[0];
  }

  /**
   * Formats a Date to a human-readable UTC display string.
   */
  public static dateToDisplayString(value: Date | undefined): string | undefined {
    if (value === undefined) {
      return undefined;
    }
    return value.toUTCString().replace('GMT', 'UTC');
  }

  /**
   * Creates a Date from an ISO string when supplied.
   */
  public static dateFromString(value: string | undefined): Date | undefined {
    return !value ? undefined : new Date(value);
  }

  /**
   * Converts a SolIcon enum value to its string token.
   */
  public static iconToString(value: SolIcon | undefined): string | undefined {
    return value === undefined ? undefined : (value as string);
  }

  /**
   * Looks up a SolIcon enum value from a serialized string.
   */
  public static iconFromString(value: string | undefined): SolIcon | undefined {
    return !value ? undefined : EnumTools.fromString(SolIcon, value);
  }

  /**
   * Serializes an array to JSON using the provided SolType serializer.
   */
  public static arrayToString<V>(v: V[] | undefined, type: SolType<V>): string | undefined {
    if (v === undefined) {
      return undefined;
    }
    const stringArray = v.map(item => type.toString(item));
    return JSON.stringify(stringArray);
  }

  /**
   * Parses a JSON array string using the supplied SolType parser.
   */
  public static stringToArray<V>(text: string | undefined, type: SolType<V>): V[] | undefined {
    if (text === undefined) {
      return undefined;
    }
    const stringArray = JSON.parse(text) as string[];
    return stringArray.map(item => type.fromString(item)) as V[];
  }

  /**
   * Serializes a single value according to its SolType.
   */
  public static serializeValue<V>(type: SolType<V>, value: V | undefined): any {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return type.toString(value);
  }

  private static deepEquals(a: any, b: any, rendered: boolean): boolean {
    if (a === b) {
      return true;
    }
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      if (a.constructor !== b.constructor) {
        return false;
      }
      if (Array.isArray(a)) {
        return ObjectTools.arrayEquals(a, b, rendered);
      }
      if (a.constructor === Date) {
        return a.getTime() === b.getTime();
      }
      if (a.constructor === RegExp) {
        return a.source === b.source && a.flags === b.flags;
      }
      if (a.constructor === Map) {
        return ObjectTools.mapEquals(a, b, rendered);
      }
      if (a.constructor === Set) {
        return ObjectTools.setEquals(a, b, rendered);
      }
      if (rendered && a.valueOf !== Object.prototype.valueOf) {
        return a.valueOf() === b.valueOf();
      }
      if (rendered && a.toString !== Object.prototype.toString) {
        return a.toString() === b.toString();
      }
      return ObjectTools.objectEquals(a, b, rendered);
    }

    // True when both are NaN, false otherwise.
    return a !== a && b !== b;
  }

  private static arrayEquals(a: any[], b: any[], rendered: boolean): boolean {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = a.length; i-- !== 0;) {
      if (!ObjectTools.deepEquals(a[i], b[i], rendered)) {
        return false;
      }
    }
    return true;
  }

  private static mapEquals(a: Map<any, any>, b: Map<any, any>, rendered: boolean): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a) {
      if (!b.has(key) || !ObjectTools.deepEquals(value, b.get(key), rendered)) {
        return false;
      }
    }
    return true;
  }

  private static setEquals(a: Set<any>, b: Set<any>, rendered: boolean): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const value of a) {
      if (b.has(value)) {
        continue;
      }
      if (!value || typeof value !== 'object' || !ObjectTools.hasEqualMember(b, value, rendered)) {
        return false;
      }
    }
    return true;
  }

  private static hasEqualMember(set: Set<any>, value: any, rendered: boolean): boolean {
    for (const candidate of set) {
      if (ObjectTools.deepEquals(value, candidate, rendered)) {
        return true;
      }
    }
    return false;
  }

  private static objectEquals(a: any, b: any, rendered: boolean): boolean {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) {
      return false;
    }
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        return false;
      }
    }
    for (const key of keys) {
      if (!ObjectTools.deepEquals(a[key], b[key], rendered)) {
        return false;
      }
    }
    return true;
  }
}
